import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Mail, ArrowLeft, Shield } from 'lucide-react'
import UniversalHeader from '@/components/shared/UniversalHeader'
import UniversalFooter from '@/components/shared/UniversalFooter'
import { useToast } from '@/hooks/use-toast'
import { authAPI } from '@/lib/api'

// Add mobile-friendly styles and autofill detection
const mobileStyles = `
  @media (max-width: 640px) {
    input[type="email"] {
      font-size: 16px !important;
    }

    /* Prevent zoom on focus for iOS */
    input[type="email"]:focus {
      font-size: 16px !important;
    }
  }

  /* Autofill detection and styling */
  input:-webkit-autofill {
    background-color: white !important;
    -webkit-box-shadow: 0 0 0 1000px white inset !important;
    color: #000000 !important;
    -webkit-text-fill-color: #000000 !important;
  }

  input:-webkit-autofill:focus {
    background-color: white !important;
    -webkit-box-shadow: 0 0 0 1000px white inset !important;
    color: #000000 !important;
    -webkit-text-fill-color: #000000 !important;
  }

  /* Ensure all inputs have black text */
  input {
    color: #000000 !important;
    -webkit-text-fill-color: #000000 !important;
    font-weight: 500 !important;
  }

  input:focus {
    color: #000000 !important;
    -webkit-text-fill-color: #000000 !important;
    font-weight: 500 !important;
  }

  /* Forgot password page specific input styling */
  #forgot-password-page input {
    color: #000000 !important;
    -webkit-text-fill-color: #000000 !important;
    font-weight: 500 !important;
  }

  #forgot-password-page input:focus {
    color: #000000 !important;
    -webkit-text-fill-color: #000000 !important;
    font-weight: 500 !important;
  }

  /* Fainter placeholder text */
  #forgot-password-page input::placeholder {
    color: #9ca3af !important;
    opacity: 0.5 !important;
  }

  #forgot-password-page input:focus::placeholder {
    color: #d1d5db !important;
    opacity: 0.4 !important;
  }
`;

if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = mobileStyles;
  document.head.appendChild(style);
}

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const { toast } = useToast()

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const response = await authAPI.forgotPassword(email)

      if (response.success) {
        setIsSubmitted(true)
        toast({
          title: "Reset email sent",
          description: "A password reset email has been sent to your inbox.",
        })
      } else {
        toast({
          title: "Failed to send",
          description: response.message || "Failed to send reset email. Please try again.",
          variant: "destructive",
        })
      }
    } catch (error) {
      toast({
        title: "Failed to send",
        description: "Failed to send reset email. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div id="forgot-password-page" className="min-h-screen flex flex-col bg-white overflow-hidden">
      <UniversalHeader title="CareVault" />

      <div className="flex-1 container mx-auto px-4 py-8 lg:py-12 flex items-center justify-center">
        <div className="w-full flex flex-col lg:flex-row bg-white rounded-[2rem] shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-800">
          {/* Left Side - Brand & Visuals (The "Colored" Section) */}
          <div className="hidden lg:flex w-1/2 relative overflow-hidden items-center justify-center p-12 text-white bg-[#0F172A] min-h-[600px]">
            {/* Realistic Image Background */}
            <div className="absolute inset-0">
              <img
                src="/assets/images/hero_image.png"
                alt="Healthcare Professional"
                className="w-full h-full object-cover opacity-40 mix-blend-overlay"
              />
              {/* Navy Blue Overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#0F172A] opacity-90" />
            </div>

            <div className="absolute inset-0 bg-[url('/assets/pattern.svg')] opacity-10 mix-blend-overlay" />

            <div className="relative z-10 max-w-lg text-center space-y-6">
              {/* Logo Container - White for visibility */}
              <div className="inline-flex items-center justify-center w-28 h-28 rounded-3xl bg-white shadow-2xl mb-8 border-4 border-white/20 animate-float">
                <img src="/assets/images/carevault_logo_modern.png" alt="CareVault Logo" className="w-20 h-20 object-contain" />
              </div>
              <h2 className="text-4xl font-bold tracking-tight shadow-sm text-white">Account Recovery,<br />Secure & Simple.</h2>
              <p className="text-lg text-blue-100 font-medium leading-relaxed drop-shadow-md">
                Don't worry, it happens. We'll help you get back to your secure health vault in no time.
              </p>
            </div>
          </div>

          {/* Right Side - Forgot Password Form (The "White" Section) */}
          <div className="w-full lg:w-1/2 flex items-center justify-center p-8 sm:p-12 relative bg-white">
            <div className="w-full max-w-[420px] space-y-8">
              {!isSubmitted ? (
                <>
                  <div className="text-center lg:text-left mb-8">
                    <h1 className="text-3xl font-bold text-[#1F303B] mb-3">
                      Forgot Password?
                    </h1>
                    <p className="text-gray-500 text-base">
                      Enter your email address and we'll send you a link to reset your password.
                    </p>
                  </div>

                  <form onSubmit={handleForgotPassword} className="space-y-6">
                    <div className="space-y-5">
                      <div className="animate-slide-in-right space-y-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2 ml-1">
                            Email Address
                          </label>
                          <div className="relative group">
                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-[#D64463] transition-colors" />
                            <input
                              type="email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              className="w-full bg-gray-50 border-2 border-gray-100 focus:bg-white focus:border-[#D64463] rounded-2xl py-4 pl-12 pr-4 outline-none transition-all font-medium text-gray-900 placeholder:text-gray-400 shadow-sm"
                              placeholder="name@example.com"
                              required
                              autoFocus
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="pt-4">
                      <Button
                        type="submit"
                        className="w-full h-14 bg-gradient-to-r from-[#D64463] to-[#9C27B0] hover:opacity-90 text-white text-lg font-semibold rounded-2xl shadow-lg shadow-[#D64463]/25 transition-all hover:scale-[1.02] active:scale-[0.98]"
                        disabled={isLoading || !email}
                      >
                        {isLoading ? (
                          <span className="flex items-center gap-2">
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Sending Link...
                          </span>
                        ) : (
                          "Send Reset Link"
                        )}
                      </Button>
                    </div>

                    <div className="text-center pt-2">
                      <Link to="/login" className="inline-flex items-center text-sm font-semibold text-gray-500 hover:text-[#D64463] transition-colors group">
                        <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
                        Back to Login
                      </Link>
                    </div>
                  </form>
                </>
              ) : (
                <div className="text-center space-y-6 animate-fade-in-up">
                  <div className="flex justify-center">
                    <div className="bg-emerald-50 p-6 rounded-full shadow-lg border border-emerald-100">
                      <Shield className="h-12 w-12 text-emerald-500" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      Check Your Email
                    </h3>
                    <p className="text-gray-600 text-lg leading-relaxed">
                      We've sent a password reset link to <br />
                      <strong className="text-[#1F303B]">{email}</strong>
                    </p>
                    <p className="text-gray-500 text-sm mt-2">
                      Please check your inbox and spam folder.
                    </p>
                  </div>

                  <div className="space-y-4 pt-6">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleForgotPassword}
                      disabled={isLoading}
                      className="w-full h-12 text-gray-700 hover:text-[#D64463] border-2 border-gray-100 hover:border-[#D64463]/30 rounded-xl font-semibold"
                    >
                      {isLoading ? "Resending..." : "Resend Email"}
                    </Button>

                    <Link to="/login" className="block text-sm font-semibold text-gray-500 hover:text-[#D64463] transition-colors">
                      Back to Login
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="bg-[#0F172A] w-full mt-auto">
        <UniversalFooter />
      </div>
    </div>
  )
}

export default ForgotPasswordPage